<script lang="ts" setup>
  import confetti from 'canvas-confetti';
  import { computed, onMounted, ref, watch } from 'vue';
  import type { GameOverView } from '../../../../shared/api/gameOver.ts';
  import { MobileViewLayout } from '../../../../shared/ui/mobile-view-layout';
  import GameHeader from '../GameHeader.vue';
  import RoundResultList from '../round/RoundResultList.vue';
  import SongCardHorizontal from '../song/SongCardHorizontal.vue';
  import FeedbackButton from './FeedbackButton.vue';
  import FeedbackModal from './FeedbackModal.vue';
  import GameOverPodium, { PODIUM_SIZE } from './GameOverPodium.vue';

  const props = defineProps<{ data: GameOverView; error: string; textError?: string }>();
  const emit = defineEmits<{ feedback: [string]; feedbackClosed: [] }>();

  const showFeedbackModal = ref(false);
  const closeFeedbackModal = () => {
    showFeedbackModal.value = false;
    emit('feedbackClosed');
  };
  const savedFeedback = computed(() => props.data.feedback);
  watch(savedFeedback, closeFeedbackModal);

  const belowPodium = computed(() =>
    props.data.round.songSubmissionScores.filter((score) => score.playerStanding.placement > PODIUM_SIZE),
  );

  const confettiDurationMs = 3000;
  const confettiTokens = [
    '--color-confetti-1',
    '--color-confetti-2',
    '--color-confetti-3',
    '--color-confetti-4',
    '--color-confetti-5',
  ];

  onMounted(() => {
    const rootStyle = getComputedStyle(document.documentElement);
    const colors = confettiTokens.map((token) => rootStyle.getPropertyValue(token).trim());
    const end = Date.now() + confettiDurationMs;

    const frame = () => {
      confetti({ particleCount: 2, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 2, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  });
</script>

<template>
  <MobileViewLayout space="m" overflow="hidden">
    <template #header>
      <GameHeader :standing="data.standing">
        <FeedbackButton @click="showFeedbackModal = true" />
      </GameHeader>
    </template>

    <SongCardHorizontal :song="data.round.chooserSongSubmissionScore.songSubmission!.song" label="Current song" />

    <GameOverPodium :song-submission-scores="data.round.songSubmissionScores" />

    <RoundResultList :chooser-player="data.round.chooser" :player="data.player" :song-submission-scores="belowPodium" />

    <FeedbackModal
      v-if="showFeedbackModal"
      :error="error"
      :existing-feedback="data.feedback"
      :text-error="textError"
      @close="closeFeedbackModal"
      @submit="$emit('feedback', $event)"
    />
  </MobileViewLayout>
</template>
